import { useQuery } from '@apollo/client/react';
import { type FunctionComponent, useState } from 'react';
import {
	type FindCustomerInput,
	type FindCustomerQuery,
	type FindCustomerQueryVariables
} from '../../../__generated__/graphql-client-types';
import { useInternalCustomerLogin } from '../../../hooks/apollo/authentication/authentication.hooks';
import { useHandleMutationResult } from '../../../hooks/general/handle-mutation-result.hooks';
import { FIND_CUSTOMER } from '../../../queries/internal-only/internal-only.queries';
import { doesWindowExist } from '../../../utils/dom/dom.utils';
import { Loading } from '../../common-components/loading/loading.component';
import { PanelComponent } from '../../common-components/panel-component/panel-component.component';
import { CustomerList } from '../customer-list/customer-list.component';

export type LoginCustomerListProps = {
	searchCriteria: FindCustomerInput;
	onLoginSuccess: () => void;
};

export const LoginCustomerList: FunctionComponent<LoginCustomerListProps> = ({ searchCriteria, onLoginSuccess }) => {
	const [loggingInUser, setLoggingInUser] = useState(false);
	const { internalLogin } = useInternalCustomerLogin();
	const { handleException } = useHandleMutationResult();
	const { data } = useQuery<FindCustomerQuery, FindCustomerQueryVariables>(FIND_CUSTOMER, {
		variables: { input: searchCriteria },
		// disable server-side rendering since this is an internal tool only and we
		// do some locale specific date formatting for order dates
		ssr: doesWindowExist()
	});

	const customerSelected = (customerId) => {
		setLoggingInUser(true);
		internalLogin(customerId)
			.then(() => {
				onLoginSuccess();
			})
			.catch(handleException)
			.finally(() => setLoggingInUser(false));
	};

	const findResults = data?.findCustomer;

	return (
		<>
			{!findResults || loggingInUser ? (
				<div className="f1 tc">
					<p>{loggingInUser ? 'Logging in...' : 'Searching...'}</p>
					<Loading />
				</div>
			) : (
				<div data-testid="search-results">
					<PanelComponent headingContent={`Found ${findResults.accounts.length} customer accounts matching your search criteria`}>
						<CustomerList searchResults={findResults.accounts} onSelectCustomer={customerSelected} />
					</PanelComponent>
					<PanelComponent headingContent={`Found ${findResults.guests.length} guest accounts matching your search criteria`}>
						<CustomerList searchResults={findResults.guests} />
					</PanelComponent>
				</div>
			)}
		</>
	);
};
